import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { searchNames } from '../lib/search'
import { Empty } from './ui'

export interface PickerPerson {
  id: string
  name: string
  /** Shown beside the name — the balance, so you see it before picking. */
  note?: string
  noteTone?: 'due' | 'clear'
}

/**
 * Name picker for a list too long to scroll.
 *
 * A plain dropdown stops working somewhere past thirty names, and this shop has
 * seventy. Three things replace it:
 *
 *   - opened with nothing typed, it shows the people dealt with most recently,
 *     which on a normal day is the whole job and needs no typing at all
 *   - every row carries that person's balance, because seeing that somebody
 *     already owes 23,950 is exactly the moment that figure is useful, and it
 *     saves a trip to the کھاتہ page to look it up
 *   - typing filters across BOTH scripts, so a customer entered from the Urdu
 *     keyboard is still found by someone typing Latin letters, and the other way
 *     round
 *
 * The search box is deliberately NOT focused when the panel opens on a touch
 * screen. Focusing it summons the on-screen keyboard, which covers half a tablet
 * and hides the recent list underneath it — the list that usually has the answer
 * already.
 */
export function PersonPicker({
  label,
  value,
  people,
  recentIds = [],
  onChange,
  noun = 'گاہک',
  vendor,
  hint,
  clearLabel,
  triggerLabel,
  loading = false,
}: {
  label: ReactNode
  value: string | null
  people: PickerPerson[]
  recentIds?: string[]
  onChange: (id: string | null) => void
  noun?: string
  vendor?: boolean
  hint?: string
  /**
   * Turns the picker into a filter: adds a row that selects nobody, and names
   * the empty state after it. Used where "everyone" is a real answer rather than
   * a missing one, as on روزنامچہ — and for a cash lot line, where nobody is the
   * whole point.
   */
  clearLabel?: string
  /**
   * What the closed trigger says, instead of the chosen name.
   *
   * <p>For the places where this picker is not how the choice is displayed but
   * only the way in to the full list - the «اور» chip on the selling form, where
   * the flower is already shown as a lit chip beside it, and the buyer card,
   * where the name and the balance are spelled out above. Repeating the name in
   * the trigger there says the same thing twice and leaves nothing saying what
   * the control does.
   */
  triggerLabel?: string
  /** True while the names are still arriving. See PickerData. */
  loading?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selected = value ? (people.find((p) => p.id === value) ?? null) : null
  const searching = query.trim().length > 0

  const { rows, headAt } = useMemo(() => {
    if (searching) {
      return { rows: searchNames(people, query), headAt: new Map<number, string>() }
    }

    // Nothing typed: the regulars first, then everyone else alphabetically.
    const byId = new Map(people.map((p) => [p.id, p]))
    const recent = recentIds
      .map((id) => byId.get(id))
      .filter((p): p is PickerPerson => Boolean(p))
    const seen = new Set(recent.map((p) => p.id))
    const rest = people
      .filter((p) => !seen.has(p.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'ur'))

    const lead: PickerPerson[] = clearLabel ? [{ id: '', name: clearLabel }] : []
    const heads = new Map<number, string>()
    if (recent.length > 0) {
      heads.set(lead.length, `پچھلے ${noun}`)
      if (rest.length > 0) heads.set(lead.length + recent.length, `سارے ${noun}`)
    }

    return { rows: [...lead, ...recent, ...rest], headAt: heads }
  }, [people, recentIds, query, searching, noun, clearLabel])

  // Only the arrow keys may move the list; see the scroll effect below.
  const steppedByKey = useRef(false)

  useEffect(() => {
    setActive(0)
    if (listRef.current) listRef.current.scrollTop = 0
  }, [query])

  // Close on a tap outside. pointerdown rather than click, so the panel is gone
  // before the control underneath it reacts.
  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    return () => document.removeEventListener('pointerdown', away)
  }, [open])

  useEffect(() => {
    if (!open) return
    // Mouse: go straight to typing. Touch: leave the keyboard down.
    if (!window.matchMedia('(pointer: coarse)').matches) inputRef.current?.focus()
  }, [open])

  // Keeps the arrow-key cursor in view — but only then. scrollIntoView walks
  // every scrollable ancestor including the page itself, so running it as the
  // panel opens drags the whole form out from under the picker.
  useEffect(() => {
    if (!steppedByKey.current) return
    steppedByKey.current = false
    listRef.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  function choose(personId: string) {
    onChange(personId === '' ? null : personId)
    setQuery('')
    setOpen(false)
  }

  function onKey(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (rows.length === 0) return
      const step = event.key === 'ArrowDown' ? 1 : -1
      steppedByKey.current = true
      setActive((i) => (i + step + rows.length) % rows.length)
      return
    }
    if (event.key === 'Enter' && rows[active]) {
      event.preventDefault()
      choose(rows[active].id)
    }
  }

  return (
    <div className="field">
      <label>{label}</label>
      <div
        className={`picker${open ? ' is-open' : ''}${vendor ? ' is-vendor' : ''}`}
        ref={wrapRef}
      >
        <button
          type="button"
          className={`picker__value${selected ? '' : ' is-empty'}`}
          // Opens, never toggles. A touch screen can deliver both a pointer and
          // a compatibility mouse click for one tap, and a toggle would shut the
          // list again the instant it appeared. Tapping away, Escape, or picking
          // a name all close it.
          onClick={() => setOpen(true)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={selected && !triggerLabel ? 'picker__name' : 'muted'}>
            {triggerLabel ?? (selected ? selected.name : (clearLabel ?? `— ${noun} چنیں —`))}
          </span>
          {!triggerLabel && <span className="picker__swap">{selected ? 'بدلیں' : '▾'}</span>}
        </button>

        {open && (
          <div className="picker__panel">
            <div className="picker__search">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onKey}
                placeholder="نام لکھیں — اردو یا انگریزی"
                autoComplete="off"
                spellCheck={false}
                // Either script may be typed here, so let the box follow
                // whichever one it is given rather than forcing the page's RTL.
                dir="auto"
                aria-label="نام تلاش کریں"
              />
            </div>

            <div className="picker__list" ref={listRef} role="listbox">
              {rows.map((person, i) => (
                <Fragment key={person.id || 'none'}>
                  {headAt.has(i) && <div className="picker__head">{headAt.get(i)}</div>}
                  <button
                    type="button"
                    role="option"
                    aria-selected={person.id === value}
                    className={`picker__opt${i === active ? ' is-active' : ''}${
                      person.id === value ? ' is-chosen' : ''
                    }`}
                    onClick={() => choose(person.id)}
                    onPointerEnter={() => setActive(i)}
                  >
                    <span className="picker__opt-name">{person.name}</span>
                    {person.note !== undefined && (
                      <span
                        className={`picker__note${
                          person.noteTone ? ` amount-${person.noteTone}` : ''
                        }`}
                      >
                        {person.note}
                      </span>
                    )}
                  </button>
                </Fragment>
              ))}

              {/*
                An empty list means two different things, and saying the wrong
                one is how a working search looks broken: a name typed before
                the list has arrived found nothing, and the panel confidently
                announced that nobody is called that.
              */}
              {rows.length === 0 && loading && <Empty>نام آ رہے ہیں…</Empty>}

              {rows.length === 0 && !loading && (
                <Empty>
                  «{query.trim()}» جیسا کوئی نام نہیں ملا۔
                  <br />
                  <span className="small">اردو یا انگریزی، دونوں طرح لکھ کر دیکھ لیں۔</span>
                </Empty>
              )}
            </div>

            {searching && rows.length > 0 && (
              <div className="picker__count">
                {people.length} میں سے {rows.length}
              </div>
            )}
          </div>
        )}
      </div>

      {hint && <div className="field__hint">{hint}</div>}
    </div>
  )
}
